/*******
x axis: -14 to 0, Attack Angle. But this will be converted to 0 to 14 to simplify plotting since the actual numbers aren't displayed
y axis: 0 to 16, Shaft Lean
Note: if a point falls outside of this range, then the boundary is adjusted accordingly and there are offset calculations to account for the new position on the canvas
Note: the browser plots the canvas points start in top left corner (0,0), so I need to flip the shaft lean since it goes from 16 to 0
 0   |
     |
     |
     |
-14  ----------
     16        0
 */

import { useState, useEffect, useRef } from 'react';

class CanvasBoundary {
  constructor(aa, sl) {
    this.aa = aa;
    this.sl = sl;
  }
}

class Boundary {
  constructor(min, max) {
    this.min = min;
    this.max = max;
  }
}

//sl = x axis; aa = y axis
class Point {
  constructor(sl, aa){
    this.sl = sl;
    this.aa = aa;
  }
}

export default function FittingCanvas(props) {
  let shotsToPlot = props.shotstoplot;
  const intl = props.intl;
  const intlShallow = intl.formatMessage({id: "now.hitting.shallow"});
  const intlNeutral = intl.formatMessage({id: "now.hitting.neutral"});
  const intlSteep = intl.formatMessage({id: "now.hitting.steep"});
  const intlFull = intl.formatMessage({id: "shot.selection.full.chart"});
  const intlFullAbbr = intl.formatMessage({id: "shot.selection.full.abbr"});
  const intlPitch = intl.formatMessage({id: "shot.selection.pitch.chart"});
  const intlPitchAbbr = intl.formatMessage({id: "shot.selection.pitch.abbr"});
  const intlOpenFace = intl.formatMessage({id: "shot.selection.open.chart"});
  const intlOpenFaceAbbr = intl.formatMessage({id: "shot.selection.open.abbr"});

  const [ctx, setCtx] = useState({});

  const canvasRef = useRef(null)

  //keeps canvas drawing ratios clean
  //http://www.petecorey.com/blog/2019/08/19/animating-a-canvas-with-react-hooks/
  const getPixelRatio = context => {
    var backingStore =
      context.backingStorePixelRatio ||
      context.webkitBackingStorePixelRatio ||
      context.mozBackingStorePixelRatio ||
      context.msBackingStorePixelRatio ||
      context.oBackingStorePixelRatio ||
      context.backingStorePixelRatio ||
      1;

    return (window.devicePixelRatio || 1) / backingStore;
  };

  useEffect(() => {
    const canvas = canvasRef.current;
    const context = canvas.getContext('2d');
    setCtx(context);

    let ratio = getPixelRatio(context);
    let width = getComputedStyle(canvas)
      .getPropertyValue('width')
      .slice(0, -2);
    let height = getComputedStyle(canvas)
      .getPropertyValue('height')
      .slice(0, -2);

    canvas.width = width * ratio;
    canvas.height = height * ratio;
    canvas.style.width = `${width}px`;
    canvas.style.height = `${height}px`;

    //get the default min/max coordinates: attack angle: 0 to 14;  shaft lean: 0 to 16
    let canvasBoundary = new CanvasBoundary(new Boundary(0, 14), new Boundary(0, 16));
    getMinMaxCoordinates('F', canvasBoundary);
    getMinMaxCoordinates('O', canvasBoundary);
    getMinMaxCoordinates('P', canvasBoundary);

    drawBackground(context, canvasBoundary, canvas);
    drawShots(context, canvasBoundary, canvas);
  })

  //get the point on the canvas to plot, taking into account the boundary offsets
  //shaft lean = x axis (width);  attack angle = y axis (height)
  function getPointWithOffset(point, boundary, canvas){
    //how many pixels per unit?
    let aaPixels = canvas.height / (boundary.aa.max - boundary.aa.min);
    let slPixels = canvas.width / (boundary.sl.max - boundary.sl.min);
    //get the coordinate offset in units
    let newSl = point.sl - boundary.sl.min;
    let newAa = point.aa - boundary.aa.min;
    //convert from units to pixel coordinates on the canvas
    newSl = newSl * slPixels;
    newAa = newAa * aaPixels;
    //since shaft lean goes from 16 to 0, gotta flip the coordinates
    newSl = canvas.width - newSl;
    return new Point(newSl, newAa);
  }

  //Note attack angle is normally negative, so to plot it must reverse the sign
  //add a buffer of 1 so shots aren't plotted directly on the border
  function getMinMaxCoordinates(shotType, boundary) {
    let shot = shotsToPlot.get(shotType);
    if (shot && shot.totalShots > 0) {
        let avgAngle = -1* shot.attackAngle / shot.totalShots;
        let avgLean = shot.shaftLean / shot.totalShots;
      boundary.aa.min = Math.min(boundary.aa.min, avgAngle - 1);
      boundary.aa.max = Math.max(boundary.aa.max, avgAngle + 1);
      boundary.sl.min = Math.min(boundary.sl.min, avgLean - 1);
      boundary.sl.max = Math.max(boundary.sl.max, avgLean + 1);
    }
  }

  function drawBackground(ctx, boundary, canvas) {
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.fillStyle = 'lightgray'
    //middle rectangle is from attack angle -4 to -8
    let topCorner = getPointWithOffset(new Point(0, 4), boundary, canvas);
    let bottomCorner = getPointWithOffset(new Point(0, 8), boundary, canvas);
    ctx.fillRect(0, topCorner.aa, canvas.width, (bottomCorner.aa-topCorner.aa))
    ctx.fillStyle = 'grey'
    ctx.font = '24px arial';
    let textPoint = getPointWithOffset(new Point(5, 2.2), boundary, canvas);
    ctx.fillText(intlShallow, textPoint.sl, textPoint.aa);
    textPoint = getPointWithOffset(new Point(9, 6.25), boundary, canvas);
    ctx.fillText(intlNeutral, textPoint.sl, textPoint.aa);
    textPoint = getPointWithOffset(new Point(13, 10), boundary, canvas);
    ctx.fillText(intlSteep, textPoint.sl, textPoint.aa);
  }

  function drawShots(ctx, boundary, canvas) {
    let title = "";

    let shot = shotsToPlot.get("F");
    if (shot) {
      title = shot.totalShots === 1 ? intlFull : intlFullAbbr;
      drawTheShot(title, shot, ctx, boundary, canvas);
    }

    shot = shotsToPlot.get("P");
    if (shot) {
      title = shot.totalShots === 1 ? intlPitch : intlPitchAbbr;
      drawTheShot(title, shot, ctx, boundary, canvas);
    }
    shot = shotsToPlot.get("O");
    if (shot) {
      title = shot.totalShots === 1 ? intlOpenFace : intlOpenFaceAbbr;
      drawTheShot(title, shot, ctx, boundary, canvas);
    }
  }

  function drawTheShot(symbol, shot, ctx, boundary, canvas) {
    //x axis = dynamic loft
    //y axis = attack angle
    let textOffset = -.2;
    if (shot.totalShots > 0) {
      let height = ctx.canvas.height;
      let width = ctx.canvas.width;
      let radius = 100 * (Math.pow(0.75, Math.min(shot.totalShots, 6)));
      //special case b/c first shot now shows the entire word:
      if (shot.totalShots === 1) {
        switch (symbol){
          case "Full":
            textOffset = -.5;
            break;
          case "Pitch":
            textOffset = -1;
            break;
          case "Open Face":
            textOffset = -2;
            break;
        }
      }

      //get average for all shots
      let shaftLean = shot.shaftLean / shot.totalShots;
      let attackAngle = -1* shot.attackAngle / shot.totalShots;

      ctx.beginPath();
      ctx.strokeStyle = 'black';
      let point = getPointWithOffset(new Point(shaftLean, attackAngle), boundary, canvas);
      ctx.arc(point.sl, point.aa, radius, 0, 2 * Math.PI, true);

      ctx.fillStyle = 'black';
      ctx.font = '22px arial';
      point = getPointWithOffset(new Point(shaftLean - textOffset, attackAngle+.25), boundary, canvas);
       ctx.fillText(symbol, point.sl, point.aa);
      ctx.stroke();
    }

  }

  return (
    <>
      <canvas ref={canvasRef} className="canvas-fitting" {...props} /><br />
    </>
  )
}
